import { GOOGLE_MAPS_STYLES } from "@utils/googleMaps";

interface MapLocation {
  address: string;
  country: string;
  name?: string;
  url?: string;
}

interface MapState {
  googleApiKey: string;
  locations: MapLocation[];
  directionLinkText?: string;
  isOverview?: boolean;
}

declare global {
  interface Window {
    onGoogleMapsApiLoaded: () => void;
    google: typeof google;
  }
}

export class MapModule {
  private static readonly DEFAULT_MAP_LAT = 47.54493075438197;
  private static readonly DEFAULT_MAP_LNG = 7.596123218536377;
  private static readonly MARKER_IMG_HEIGHT = 60;
  private static readonly MARKER_IMG_WIDTH = 43;

  private el: HTMLElement;
  private abortController: AbortController;
  private state: MapState;
  private map: google.maps.Map | null = null;
  private mapCanvas: HTMLElement | null;
  private markerUrlBase: string;
  private apiKey: string;
  private isOverview: boolean;
  private locations: MapLocation[];
  private directionLinkText: string;
  private static apiIsLoading = false;
  private isInitialized = false;
  private markers: google.maps.Marker[] = [];

  constructor(el: HTMLElement) {
    this.el = el;
    this.abortController = new AbortController();
    this.state = this.loadState();
    this.mapCanvas = this.el.querySelector(".map");
    this.markerUrlBase = this.el.dataset.mapMarkerUrl || "";
    this.apiKey = this.state.googleApiKey;
    this.isOverview = this.state.isOverview || false;
    this.locations = this.state.locations;
    this.directionLinkText = this.state.directionLinkText || "";
  }

  init(): void {
    const signal = this.abortController.signal;

    document.addEventListener("googleMapsApiReady", this.onGoogleMapsApiReady as EventListener, { signal });
    document.addEventListener("googleMapsApiIsLoading", this.onGoogleMapsApiIsLoading, { signal });

    this.sync();
  }

  private loadState(): MapState {
    if (this.el.hasAttribute("data-state")) {
      return JSON.parse(this.el.getAttribute("data-state")!);
    }
    return { googleApiKey: "", locations: [] };
  }

  private sync(): void {
    if (!this.mapCanvas) return;

    if (typeof window.google !== "object" || typeof window.google?.maps !== "object") {
      if (!MapModule.apiIsLoading) {
        this.loadGoogleMapsApi();
      }
    } else {
      this.initializeGoogleMap();
    }
  }

  private loadGoogleMapsApi(): void {
    document.dispatchEvent(new CustomEvent("googleMapsApiIsLoading"));

    let apiKeyGetParam = "";
    if (this.apiKey !== "") {
      apiKeyGetParam = "&key=" + this.apiKey;
    }

    const script = document.createElement("script");
    script.type = "text/javascript";
    script.src =
      "https://maps.googleapis.com/maps/api/js?v=3&callback=onGoogleMapsApiLoaded" +
      apiKeyGetParam;
    document.body.appendChild(script);
  }

  private onGoogleMapsApiIsLoading = (): void => {
    MapModule.apiIsLoading = true;
  };

  private onGoogleMapsApiReady = (event: CustomEvent<{ google: typeof google }>): void => {
    MapModule.apiIsLoading = false;
    const g = event.detail?.google;
    if (!g) return;
    this.initializeGoogleMap();
  };

  private initializeGoogleMap(): void {
    if (this.isInitialized || !this.mapCanvas) return;

    const googleMaps = window.google.maps;

    this.map = new googleMaps.Map(this.mapCanvas, {
      center: { lat: MapModule.DEFAULT_MAP_LAT, lng: MapModule.DEFAULT_MAP_LNG },
      disableDefaultUI: true,
      zoom: 3,
      maxZoom: 16,
      zoomControl: false,
      streetViewControl: false,
      styles: GOOGLE_MAPS_STYLES,
    });

    const latlngbounds = new googleMaps.LatLngBounds();

    const promises = this.locations.map((location) => {
      if (!location?.address?.length) return Promise.resolve();

      return new Promise<void>((resolve, reject) => {
        new googleMaps.Geocoder().geocode(
          { address: location.address },
          (results: google.maps.GeocoderResult[] | null, status: google.maps.GeocoderStatus) => {
            if (status === "OK" && results) {
              const latLng = {
                lat: results[0].geometry.location.lat(),
                lng: results[0].geometry.location.lng(),
              };
              const marker = new googleMaps.Marker({
                position: latLng,
                map: this.map!,
                icon: {
                  url: this.toMarkerUrl("/resources/media/googlemaps/group/poi-education.png"),
                  scaledSize: new googleMaps.Size(MapModule.MARKER_IMG_WIDTH, MapModule.MARKER_IMG_HEIGHT),
                },
              });

              this.markers.push(marker);
              latlngbounds.extend(latLng);

              if (location.name) {
                const e = MapModule.escapeHtml.bind(MapModule);
                const safeUrl = /^https?:\/\//i.test(location.url ?? "") ? location.url! : "#";
                const content = `<div class="mapInfoWindow"><div class="infoboxbody"><p><b>${e(location.name)}</b><br>${e(location.address)}<br>${e(location.country)}<br><a target="_blank" rel="noopener noreferrer" href="${e(safeUrl)}">${e(this.directionLinkText)}</a></p></div></div>`;
                const InfoBox = require("google-maps-infobox").InfoBox;
                const infoBox = new InfoBox({
                  map: this.map!,
                  content,
                  pixelOffset: new googleMaps.Size(-150, -80),
                  alignBottom: true,
                  maxWidth: 300,
                  maxHeight: 100,
                  closeBoxMargin: "10px",
                });

                googleMaps.event.addListener(marker, "click", () => {
                  infoBox.open(this.map!, marker);
                });

                googleMaps.event.addListener(this.map!, "click", () => {
                  infoBox.close();
                });
              }

              googleMaps.event.addDomListener(window, "resize", () => {
                this.map!.setCenter(marker.getPosition()!);
              });

              resolve();
            } else {
              console.error("Geocode was not successful for the following reason: " + status);
              reject(status);
            }
          },
        );

        if (this.isOverview) {
          new googleMaps.Geocoder().geocode(
            { address: location.country },
            (results: google.maps.GeocoderResult[] | null, status: google.maps.GeocoderStatus) => {
              if (status === "OK" && results) {
                this.map!.fitBounds(results[0].geometry.viewport);
              } else {
                console.error("Geocode was not successful for the following reason: " + status);
              }
            },
          );
        }
      });
    });

    Promise.allSettled(promises).then(() => {
      if (!this.isOverview && this.map) {
        this.map.setCenter(latlngbounds.getCenter());
        this.map.fitBounds(latlngbounds);
      }
    });

    this.isInitialized = true;
  }

  private static escapeHtml(str: string): string {
    return str
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");
  }

  private toMarkerUrl(url: string): string {
    return url.indexOf(this.markerUrlBase) === -1 ? this.markerUrlBase + url : url;
  }

  destroy(): void {
    this.abortController.abort();
  }

  static initAll(root: Document | HTMLElement = document): void {
    root
      .querySelectorAll<HTMLElement>('[data-component="map"]')
      .forEach((el) => {
        new MapModule(el).init();
      });
  }
}

function onGoogleMapsApiLoaded(): void {
  document.dispatchEvent(new CustomEvent("googleMapsApiReady", { detail: { google: window.google } }));
}

window.onGoogleMapsApiLoaded = onGoogleMapsApiLoaded;
